import React, { memo, useMemo, useEffect, useState } from 'react';
import { Typography } from 'antd';
import { useApp } from '../context/AppContext';
import './TelegramLoader.css';

const { Paragraph } = Typography;

const LoadingOverlay = memo(({ 
  isVisible, 
  loadingText,
  subtitle,
  showProgress = true,
  showDots = true,
  zIndex = 1000
}) => {
  const { loadingStates, t } = useApp();
  const [currentText, setCurrentText] = useState('');

  // Определяем текст загрузки на основе активных состояний
  useEffect(() => {
    if (!isVisible) return;

    const loadingTexts = {
      user:          [t('overlay_user_1'),     t('overlay_user_2'),     t('overlay_user_3')],
      subscriptions: [t('overlay_subs_1'),     t('overlay_subs_2'),     t('overlay_subs_3')],
      operations:    [t('overlay_ops_1'),      t('overlay_ops_2'),      t('overlay_ops_3')],
      purchase:      [t('overlay_purchase_1'), t('overlay_purchase_2'), t('overlay_purchase_3')],
    };

    const activeLoadingType = Object.entries(loadingStates).find(([, isActive]) => isActive)?.[0];
    
    if (activeLoadingType && loadingTexts[activeLoadingType]) {
      const texts = loadingTexts[activeLoadingType];
      let currentIndex = 0;
      setCurrentText(texts[currentIndex]);

      const interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % texts.length;
        setCurrentText(texts[currentIndex]);
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setCurrentText(loadingText || t('overlay_loading'));
    }
  }, [isVisible, loadingStates, loadingText, t]);

  const overlayStyles = useMemo(() => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '24px',
    background: 'var(--tg-theme-bg-color, rgba(255, 255, 255, 0.95))',
    backdropFilter: 'blur(8px)',
    color: 'var(--tg-theme-text-color, #000000)',
    zIndex,
    opacity: isVisible ? 1 : 0,
    visibility: isVisible ? 'visible' : 'hidden',
    transition: 'opacity 0.3s ease, visibility 0.3s ease',
    overflow: 'hidden'
  }), [isVisible, zIndex]);

  const titleStyles = useMemo(() => ({
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--tg-theme-text-color, #000000)',
    textAlign: 'center',
    margin: 0,
    opacity: isVisible ? 1 : 0,
    transition: 'opacity 0.3s ease'
  }), [isVisible]);

  const subtitleStyles = useMemo(() => ({
    fontSize: '14px',
    color: 'var(--tg-theme-hint-color, #999999)',
    textAlign: 'center',
    margin: 0,
    opacity: isVisible ? 0.8 : 0,
    transition: 'opacity 0.3s ease'
  }), [isVisible]);

  if (!isVisible) return null;

  return (
    <div style={overlayStyles}>
      {/* Фоновые анимированные элементы */}
      <div className="telegram-bg-animation">
        <div className="floating-circle circle-1"></div>
        <div className="floating-circle circle-2"></div>
        <div className="floating-circle circle-3"></div>
        <div className="floating-circle circle-4"></div>
      </div>
      
      {/* Главный логотип/иконка */}
      <div className="telegram-logo-container">
        <div className="telegram-logo">
          <div className="telegram-inner-circle">
            <div className="telegram-plane">
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none"
              >
                <path
                  d="M2 12L22 2L18 22L12 18L8 14L2 12Z"
                  stroke="var(--tg-theme-button-text-color, #ffffff)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 18L18 22L22 2L12 18Z"
                  fill="var(--tg-theme-button-text-color, #ffffff)"
                  opacity="0.3"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Прогресс бар */}
      {showProgress && (
        <div className="telegram-progress-container">
          <div className="telegram-progress-bar">
            <div className="telegram-progress-fill"></div>
          </div>
        </div>
      )}

      {/* Текст */}
      <div className="telegram-text-container">
        <Paragraph style={titleStyles}>
          {currentText}
        </Paragraph>
        <Paragraph style={subtitleStyles}>
          {subtitle || t('overlay_wait')}
        </Paragraph>
      </div>

      {/* Пульсирующие точки */}
      {showDots && (
        <div className="telegram-dots">
          <div className="dot dot-1"></div>
          <div className="dot dot-2"></div>
          <div className="dot dot-3"></div>
        </div>
      )}
    </div>
  );
});

LoadingOverlay.displayName = 'LoadingOverlay';

export default LoadingOverlay; 